/**
 * kizashi prototype — エントリポイント
 * 画面遷移ルーターと localStorage 保存を担当する。
 * 各ゲームのロジックは games/ に委譲する。
 */

import './style.css';
import { startBalloonGame, computeBalloonScore } from './games/balloon.js';
import { startTrustGame, computeTrustScore } from './games/trust.js';
import type { BalloonTrialRecord } from './scoring/balloon.js';
import type { TrustTrialRecord } from './scoring/trust.js';
import { generateUlid } from './ulid.js';
import { createSession, submitBart, submitTrust, submitEmail } from './api.js';
import { generateReport } from './report.js';

// 受験者識別: セッション固定の仮 UUID
function getOrCreateUserId(): string {
  const key = 'kizashi_uid';
  let uid = localStorage.getItem(key);
  if (!uid) {
    uid = crypto.randomUUID();
    localStorage.setItem(key, uid);
  }
  return uid;
}

const userId = getOrCreateUserId();

// 受験者 ID（ULID）: API 送信用
const RESPONDENT_KEY = 'kizashi_respondent_id';
function getRespondentId(): string {
  return localStorage.getItem(RESPONDENT_KEY) ?? '';
}

// セッション内のゲーム結果を保持
interface SessionResults {
  balloon?: BalloonTrialRecord[];
  trust?: TrustTrialRecord[];
}
const sessionResults: SessionResults = {};

// ── 画面定義 ──────────────────────────────────────────────
type Screen = 'home' | 'balloon-instruction' | 'balloon-game'
            | 'trust-instruction' | 'trust-game' | 'results';

function showScreen(name: Screen): void {
  // 全画面を非表示にしてから対象だけ active にする
  document.querySelectorAll('.screen').forEach((el) => el.classList.remove('active'));
  const target = document.getElementById(`screen-${name}`);
  if (target) target.classList.add('active');
}

// ── パーセンタイル計算 ──────────────────────────────────────
/**
 * 正規分布の累積分布関数（Abramowitz & Stegun 近似式）
 * 精度: |error| < 7.5e-8
 */
function normalCDF(x: number): number {
  const t = 1 / (1 + 0.2316419 * Math.abs(x));
  const d = 0.3989422820 * Math.exp(-0.5 * x * x);
  const poly =
    t * (0.3193815 +
    t * (-0.3565638 +
    t * (1.7814779 +
    t * (-1.8212560 +
    t * 1.3302744))));
  const cdf = 1 - d * poly;
  return x >= 0 ? cdf : 1 - cdf;
}

/**
 * 値をパーセンタイル (0–100) に変換する（暫定正規分布使用）
 * Phase 1 データ取得後に実測参照分布で置換予定
 */
function toPercentile(value: number, mean: number, sd: number): number {
  const z = (value - mean) / sd;
  return Math.round(normalCDF(z) * 100);
}

/** パーセンタイルから帯文字列に変換 */
function toPercentileBand(pct: number): string {
  if (pct >= 80) return '上位 20%';
  if (pct >= 60) return '上位 20〜40%';
  if (pct >= 40) return '中位 40〜60%';
  if (pct >= 20) return '下位 20〜40%';
  return '下位 20%';
}

// ── 傾向文言マッピング ────────────────────────────────────
const TENDENCY_TEXT = {
  risk: {
    high:   'チャレンジや変化を好み、不確実な状況でも積極的に行動する傾向があります。',
    middle: '状況に応じてリスクを柔軟に判断し、バランスよく行動する傾向があります。',
    low:    'リスクを慎重に見極めてから行動する、堅実なスタイルを持つ傾向があります。',
  },
  trust: {
    high:   '相手を信頼して積極的に協力する、開かれた対人スタイルを持つ傾向があります。',
    middle: '相手の行動を見ながら信頼を調整していく、柔軟な対人スタイルの傾向があります。',
    low:    '慎重に相手を見極めながら信頼関係を築いていく傾向があります。',
  },
  adapt: {
    high:   '裏切りなどの否定的な経験から素早く学び、行動を調整する傾向があります。',
    middle: '経験を踏まえながら、ほどよいペースで行動を調整していく傾向があります。',
    low:    '一貫した行動スタイルを維持しながら着実に関係を築く傾向があります。',
    null:   '今回のゲームでは適応速度を算出するには十分なデータが得られませんでした。',
  },
} as const;

/** パーセンタイルから 3段階キー に変換 */
function toTendencyKey(pct: number): 'high' | 'middle' | 'low' {
  if (pct >= 60) return 'high';
  if (pct >= 40) return 'middle';
  return 'low';
}

// ── SVG レーダーチャート ───────────────────────────────────
/**
 * 3軸正三角形レーダーチャートを SVG 文字列で生成する。
 * values: [リスク志向, 信頼形成, 適応速度] それぞれ 0–100 のパーセンタイル値
 * 適応速度が null の場合は 0 扱い（表示側で注記）
 */
function buildRadarSVG(values: [number, number, number | null]): string {
  const SIZE = 260;
  const CX = SIZE / 2;
  const CY = SIZE / 2;
  const R = 88;        // 外周半径
  const LABEL_R = 112; // ラベル配置半径
  const GRIDS = 5;

  // 3軸の角度: 頂点が上、右下、左下
  const angles = [
    -Math.PI / 2,
    -Math.PI / 2 + (2 * Math.PI) / 3,
    -Math.PI / 2 + (4 * Math.PI) / 3,
  ];
  const labels = ['リスク志向', '信頼形成', '適応速度'];

  const toXY = (angle: number, radius: number) => ({
    x: CX + radius * Math.cos(angle),
    y: CY + radius * Math.sin(angle),
  });

  // グリッド描画（5段階）
  let gridLines = '';
  for (let g = 1; g <= GRIDS; g++) {
    const r = (R * g) / GRIDS;
    const pts = angles.map((a) => {
      const p = toXY(a, r);
      return `${p.x.toFixed(2)},${p.y.toFixed(2)}`;
    }).join(' ');
    gridLines += `<polygon points="${pts}" fill="none" stroke="#d8ddf5" stroke-width="1"/>`;
  }

  // 軸線
  let axisLines = '';
  angles.forEach((a) => {
    const outer = toXY(a, R);
    axisLines += `<line x1="${CX.toFixed(2)}" y1="${CY.toFixed(2)}" x2="${outer.x.toFixed(2)}" y2="${outer.y.toFixed(2)}" stroke="#d8ddf5" stroke-width="1"/>`;
  });

  // データポリゴン
  const dataValues: [number, number, number] = [
    values[0],
    values[1],
    values[2] ?? 0,
  ];
  const dataPoints = angles.map((a, i) => {
    const r = (dataValues[i] / 100) * R;
    const p = toXY(a, r);
    return `${p.x.toFixed(2)},${p.y.toFixed(2)}`;
  }).join(' ');

  const dataPolygon = `<polygon points="${dataPoints}" fill="rgba(79,110,247,0.22)" stroke="#4f6ef7" stroke-width="2" stroke-linejoin="round"/>`;

  // データ頂点ドット
  const dots = angles.map((a, i) => {
    const r = (dataValues[i] / 100) * R;
    const p = toXY(a, r);
    return `<circle cx="${p.x.toFixed(2)}" cy="${p.y.toFixed(2)}" r="4" fill="#4f6ef7"/>`;
  }).join('');

  // 軸ラベル
  const labelElems = angles.map((a, i) => {
    const p = toXY(a, LABEL_R);
    let anchor = 'middle';
    if (i === 1) anchor = 'start';
    if (i === 2) anchor = 'end';
    const dy = i === 0 ? '-0.4em' : '0.35em';
    return `<text x="${p.x.toFixed(2)}" y="${p.y.toFixed(2)}" dy="${dy}" text-anchor="${anchor}" font-size="11" fill="#444" font-family="sans-serif">${labels[i]}</text>`;
  }).join('');

  return `<svg xmlns="http://www.w3.org/2000/svg" width="${SIZE}" height="${SIZE}" viewBox="0 0 ${SIZE} ${SIZE}" aria-label="レーダーチャート">${gridLines}${axisLines}${dataPolygon}${dots}${labelElems}</svg>`;
}

// ── スコア計算（共通） ──────────────────────────────────────
interface ComputedScores {
  riskPct: number | null;
  trustPct: number | null;
  adaptPct: number | null;
  riskBand: string;
  trustBand: string;
  adaptBand: string;
  riskText: string;
  trustText: string;
  adaptText: string;
  balloonPlayed: boolean;
  trustPlayed: boolean;
}

function computeScores(): ComputedScores {
  let riskPct: number | null = null;
  let trustPct: number | null = null;
  let adaptPct: number | null = null;

  let riskBand = '';
  let trustBand = '';
  let adaptBand = '';

  let riskText = '';
  let trustText = '';
  let adaptText = '';

  let balloonPlayed = false;
  let trustPlayed = false;

  if (sessionResults.balloon && sessionResults.balloon.length > 0) {
    balloonPlayed = true;
    const s = computeBalloonScore(sessionResults.balloon);
    riskPct = toPercentile(s.avgAdjustedPumps, 40, 15);
    riskBand = toPercentileBand(riskPct);
    riskText = TENDENCY_TEXT.risk[toTendencyKey(riskPct)];
  }

  if (sessionResults.trust && sessionResults.trust.length > 0) {
    trustPlayed = true;
    const s = computeTrustScore(sessionResults.trust);
    trustPct = toPercentile(s.avgInvestRate, 0.50, 0.15);
    trustBand = toPercentileBand(trustPct);
    trustText = TENDENCY_TEXT.trust[toTendencyKey(trustPct)];

    if (s.learningSpeed !== null) {
      adaptPct = toPercentile(s.learningSpeed, 10, 15);
      adaptBand = toPercentileBand(adaptPct);
      adaptText = TENDENCY_TEXT.adapt[toTendencyKey(adaptPct)];
    } else {
      adaptText = TENDENCY_TEXT.adapt.null;
    }
  }

  return { riskPct, trustPct, adaptPct, riskBand, trustBand, adaptBand, riskText, trustText, adaptText, balloonPlayed, trustPlayed };
}

// ── 受験者タブ ─────────────────────────────────────────────
function renderCandidateTab(container: HTMLElement, scores: ComputedScores): void {
  const { riskPct, trustPct, adaptPct, riskBand, trustBand, adaptBand, riskText, trustText, adaptText, balloonPlayed, trustPlayed } = scores;

  const radarValues: [number, number, number | null] = [riskPct ?? 50, trustPct ?? 50, adaptPct];
  const showRadar = balloonPlayed || trustPlayed;

  container.innerHTML = showRadar
    ? `
      <div class="radar-wrap">
        ${buildRadarSVG(radarValues)}
      </div>
      <div class="axis-list">
        ${balloonPlayed ? `
        <div class="axis-item">
          <div class="axis-item-header">
            <span class="axis-item-label">リスク志向</span>
            <span class="pct-badge">${riskBand}</span>
          </div>
          <div class="axis-item-text">${riskText}</div>
        </div>` : ''}
        ${trustPlayed ? `
        <div class="axis-item">
          <div class="axis-item-header">
            <span class="axis-item-label">信頼形成</span>
            <span class="pct-badge">${trustBand}</span>
          </div>
          <div class="axis-item-text">${trustText}</div>
        </div>
        <div class="axis-item">
          <div class="axis-item-header">
            <span class="axis-item-label">適応速度</span>
            ${adaptPct !== null
              ? `<span class="pct-badge">${adaptBand}</span>`
              : '<span class="pct-badge pct-badge--na">データ不足</span>'}
          </div>
          <div class="axis-item-text">${adaptText}</div>
        </div>` : ''}
      </div>
    `
    : '<div class="result-no-data">ゲームをプレイすると結果が表示されます</div>';

  const emailSection = document.createElement('div');
  emailSection.className = 'email-section';
  emailSection.innerHTML = `
    <div class="email-section-title">結果をメールで受け取る（任意）</div>
    <div class="email-input-wrap">
      <input type="email" class="email-input" id="email-input"
        placeholder="example@email.com" autocomplete="email" />
      <button class="btn btn-secondary email-send-btn" id="btn-email-send">送信</button>
    </div>
    <div class="email-feedback" id="email-feedback"></div>
  `;
  container.appendChild(emailSection);

  document.getElementById('btn-email-send')!.addEventListener('click', async () => {
    const input = document.getElementById('email-input') as HTMLInputElement;
    const feedback = document.getElementById('email-feedback')!;
    const email = input.value.trim();

    if (!email || !email.includes('@')) {
      feedback.textContent = 'メールアドレスを正しく入力してください。';
      feedback.className = 'email-feedback error';
      return;
    }

    const rid = getRespondentId();
    await submitEmail(rid, email);

    feedback.textContent = '送信しました。ありがとうございます。';
    feedback.className = 'email-feedback success';
    input.disabled = true;
    (document.getElementById('btn-email-send') as HTMLButtonElement).disabled = true;
  });
}

// ── 採用担当者タブ ─────────────────────────────────────────
function renderHRTab(container: HTMLElement, scores: ComputedScores): void {
  const { riskPct, trustPct, adaptPct, balloonPlayed, trustPlayed } = scores;

  if (!balloonPlayed && !trustPlayed) {
    container.innerHTML = '<div class="result-no-data">ゲームをプレイすると判定レポートが表示されます</div>';
    return;
  }

  const report = generateReport({
    riskPct,
    trustPct,
    adaptPct,
    respondentId: userId.slice(0, 8),
    assessedAt: new Date().toLocaleString('ja-JP'),
  });

  container.innerHTML = `
    <div class="hr-report">
      <div class="hr-profile-header">
        <div class="hr-profile-badge">判定プロファイル</div>
        <div class="hr-profile-name">${report.profileName}</div>
        <div class="hr-profile-catch">${report.profileCatch}</div>
      </div>

      <div class="hr-section">
        <div class="hr-section-label">総合所見</div>
        <div class="hr-section-body">${report.summary.replace(/\n/g, '<br>')}</div>
      </div>

      <div class="hr-section">
        <div class="hr-section-label">行動特性</div>
        <ul class="hr-traits">
          ${report.traits.map(t => `<li>${t}</li>`).join('')}
        </ul>
      </div>

      <div class="hr-section">
        <div class="hr-section-label">適性が見込まれるロール</div>
        <div class="hr-roles">
          ${report.suitableRoles.map(r => `<span class="hr-role-tag">${r}</span>`).join('')}
        </div>
      </div>

      <div class="hr-section">
        <div class="hr-section-label">マネジメント上の留意点</div>
        <div class="hr-section-body">${report.managementNote}</div>
      </div>

      <div class="hr-section">
        <div class="hr-section-label">適応速度の評価</div>
        <div class="hr-section-body">${report.adaptNote}</div>
      </div>

      <div class="hr-section hr-section--detail">
        <div class="hr-section-label">軸別スコア解釈</div>
        <div class="hr-detail-item">
          <div class="hr-detail-axis">G1 リスク志向（BART）</div>
          <div class="hr-section-body">${report.riskDetail}</div>
        </div>
        <div class="hr-detail-item" style="margin-top:12px;">
          <div class="hr-detail-axis">G2 信頼形成（Trust Game）</div>
          <div class="hr-section-body">${report.trustDetail}</div>
        </div>
      </div>

      <div class="hr-disclaimer">
        ${report.disclaimer.replace(/\n/g, '<br>')}
      </div>
    </div>
  `;
}

// ── 結果画面 ──────────────────────────────────────────────
function renderResults(): void {
  const scores = computeScores();

  // localStorage に保存
  const saveKey = `kizashi_${userId}_${Date.now()}`;
  localStorage.setItem(saveKey, JSON.stringify({
    uid: userId,
    savedAt: new Date().toISOString(),
    balloon: sessionResults.balloon ?? null,
    trust: sessionResults.trust ?? null,
  }));

  // タブ切り替えの初期化
  const candidatePane = document.getElementById('result-tab-candidate')!;
  const hrPane = document.getElementById('result-tab-hr')!;

  renderCandidateTab(candidatePane, scores);

  const btnCandidate = document.getElementById('btn-tab-candidate')!;
  const btnHR = document.getElementById('btn-tab-hr')!;

  btnCandidate.addEventListener('click', () => {
    btnCandidate.classList.add('active');
    btnHR.classList.remove('active');
    candidatePane.style.display = 'block';
    hrPane.style.display = 'none';
  });

  btnHR.addEventListener('click', () => {
    btnHR.classList.add('active');
    btnCandidate.classList.remove('active');
    hrPane.style.display = 'block';
    candidatePane.style.display = 'none';
    if (!hrPane.dataset.rendered) {
      renderHRTab(hrPane, scores);
      hrPane.dataset.rendered = '1';
    }
  });
}

// ── DOM 構築 ──────────────────────────────────────────────
function buildApp(): void {
  const app = document.getElementById('app')!;
  app.innerHTML = `
    <!-- ホーム -->
    <div class="screen active" id="screen-home">
      <div class="home-logo">kizashi</div>
      <div class="home-tagline">認知・行動アセスメント β</div>

      <div class="game-card" id="card-balloon">
        <div class="game-card-badge">G1 · リスク選好</div>
        <div class="game-card-title">🎈 風船膨らまし</div>
        <div class="game-card-desc">30 回の試行でリスクへの向き合い方を測ります。約 3〜5 分。</div>
      </div>

      <div class="game-card" id="card-trust">
        <div class="game-card-badge">G2 · 信頼・公平性</div>
        <div class="game-card-title">🤝 分配ゲーム</div>
        <div class="game-card-desc">10 回の試行で相手への信頼行動と学習を測ります。約 3〜5 分。</div>
      </div>

      <div class="game-card" id="card-results" style="background:#eef0ff;">
        <div class="game-card-badge" style="background:#fff;">結果</div>
        <div class="game-card-title">📊 結果を見る</div>
        <div class="game-card-desc">プレイ済みゲームのスコアを確認できます。</div>
      </div>
    </div>

    <!-- 風船 教示 -->
    <div class="screen" id="screen-balloon-instruction">
      <div class="instruction-title">🎈 風船膨らまし</div>
      <div class="instruction-body">
        <p>これから 30 回、風船を膨らます作業をしてもらいます。</p>
        <ul>
          <li>「ポンプ」を押すたびに風船が膨らみ、1 ポイント加算されます。</li>
          <li>「保管」を押すと、その時点のポイントが確定して次の風船に移ります。</li>
          <li>膨らましすぎると風船が破裂し、その試行のポイントはゼロになります。</li>
          <li>破裂するまでのポンプ数は風船ごとに異なります（事前には分かりません）。</li>
        </ul>
        <p>正解も不正解もありません。自分のペースで進めてください。</p>
      </div>
      <button class="btn btn-primary" id="btn-balloon-start">スタート</button>
      <button class="btn btn-secondary" id="btn-balloon-back">戻る</button>
    </div>

    <!-- 風船 ゲーム -->
    <div class="screen" id="screen-balloon-game">
      <div id="balloon-game-container"></div>
    </div>

    <!-- Trust 教示 -->
    <div class="screen" id="screen-trust-instruction">
      <div class="instruction-title">🤝 分配ゲーム</div>
      <div class="instruction-body">
        <p>これから 10 回、架空のキャラクタとのやりとりをしてもらいます。</p>
        <ul>
          <li>あなたは毎回 100 ポイントを持ち、その一部または全部をキャラクタに委託できます。</li>
          <li>委託した金額は 3 倍になってキャラクタに届きます。</li>
          <li>キャラクタはそこから一部を返金します。返金額はキャラクタによって異なります。</li>
          <li>委託しなければ確実に 0 ポイントのやりとりになります。</li>
        </ul>
        <p>正解も不正解もありません。直感で進めてください。</p>
      </div>
      <button class="btn btn-primary" id="btn-trust-start">スタート</button>
      <button class="btn btn-secondary" id="btn-trust-back">戻る</button>
    </div>

    <!-- Trust ゲーム -->
    <div class="screen" id="screen-trust-game">
      <div id="trust-game-container"></div>
    </div>

    <!-- 結果 -->
    <div class="screen" id="screen-results">
      <div class="result-title">結果レポート</div>
      <div class="result-subtitle">受験者 ID: ${userId.slice(0, 8)}…</div>

      <div class="result-tabs">
        <button class="result-tab-btn active" id="btn-tab-candidate">あなたの傾向</button>
        <button class="result-tab-btn" id="btn-tab-hr">採用担当者向け</button>
      </div>

      <div id="result-tab-candidate"></div>
      <div id="result-tab-hr" style="display:none;"></div>

      <button class="btn btn-secondary" id="btn-results-home">ホームへ戻る</button>
    </div>
  `;

  // ── イベント ──
  document.getElementById('card-balloon')!.addEventListener('click', () => showScreen('balloon-instruction'));
  document.getElementById('card-trust')!.addEventListener('click', () => showScreen('trust-instruction'));
  document.getElementById('card-results')!.addEventListener('click', () => {
    renderResults();
    showScreen('results');
  });

  document.getElementById('btn-balloon-back')!.addEventListener('click', () => showScreen('home'));
  document.getElementById('btn-trust-back')!.addEventListener('click', () => showScreen('home'));
  document.getElementById('btn-results-home')!.addEventListener('click', () => showScreen('home'));

  document.getElementById('btn-balloon-start')!.addEventListener('click', () => {
    // 受験者 ID を生成して localStorage に保存し、セッション開始を記録する
    let respondentId = localStorage.getItem(RESPONDENT_KEY);
    if (!respondentId) {
      respondentId = generateUlid();
      localStorage.setItem(RESPONDENT_KEY, respondentId);
    }
    void createSession(respondentId);

    showScreen('balloon-game');
    const gameContainer = document.getElementById('balloon-game-container')!;
    startBalloonGame(gameContainer, (records) => {
      sessionResults.balloon = records;
      const rid = getRespondentId();
      if (rid) void submitBart(rid, records);
      renderResults();
      showScreen('results');
    });
  });

  document.getElementById('btn-trust-start')!.addEventListener('click', () => {
    showScreen('trust-game');
    const gameContainer = document.getElementById('trust-game-container')!;
    startTrustGame(gameContainer, (records) => {
      sessionResults.trust = records;
      const rid = getRespondentId();
      if (rid) void submitTrust(rid, records);
      renderResults();
      showScreen('results');
    });
  });
}

buildApp();
